import { cva, type VariantProps } from '@veraclins-dev/cva';

import { statusToneVariants } from './base';
import { BADGE_DEFAULT_CLASSES } from './shared-classes';
import { styleProps } from './styles';

// Badge variants — Model A status axes (variant × tone).
const badgeVariants = cva({
  base: BADGE_DEFAULT_CLASSES,
  responsive: {
    ...styleProps,
  },
  variants: {
    ...statusToneVariants.variants,
    variant: {
      ...statusToneVariants.variants.variant,
      // A count, such as the number beside a navigation item: a pill on the card fill
      // with a hairline edge and tabular monospaced digits, so a column of
      // counts lines up. The tone colours the digits only, and the size is
      // fixed (see the compound below).
      count:
        'border border-border bg-card text-card-foreground font-mono tabular-nums',
      // A quiet tag, such as "Private to your team": the hairline `border`
      // token on a transparent surface, one step below `outline`, as Button's
      // `quiet` emphasis is. The border stays the hairline in every tone; the
      // tone colours the label only, with its muted text step (see below).
      quiet: 'bg-transparent border border-border',
      // A dot with no text, such as an unread marker on a list row or a
      // "something new" marker on a button. The tone fills it (see below) and
      // the size is fixed, so `badgeSize` does not apply. It carries no text,
      // so every use needs an accessible label or `aria-hidden`.
      dot: 'border-0',
    },
    badgeSize: {
      sm: 'px-1.5 py-0.5 text-xs',
      md: 'px-2.5 py-1 text-sm',
      lg: 'px-3.5 py-1.5 text-sm',
      xl: 'px-4.5 py-2 text-base',
    },
    pill: {
      true: 'rounded-full',
      false: 'rounded-md',
    },
  },
  compoundVariants: [
    ...statusToneVariants.compoundVariants,
    // count — one fixed size, the design's small nav count. A compound so it
    // lands after `badgeSize` and wins over its padding and text size.
    {
      variant: 'count',
      className: 'px-1.75 py-0.75 text-xs leading-none',
    },
    // dot — one fixed 8px size. A compound so it lands after `badgeSize` and
    // wins over its padding. `max-w-none` lifts the base `max-w-fit`, which
    // clamps a dot with no text to zero width.
    {
      variant: 'dot',
      className: 'size-2 max-w-none p-0',
    },
    // dot — each tone's solid fill.
    { variant: 'dot', tone: 'neutral', className: 'bg-neutral' },
    { variant: 'dot', tone: 'brand', className: 'bg-primary' },
    { variant: 'dot', tone: 'danger', className: 'bg-danger' },
    { variant: 'dot', tone: 'success', className: 'bg-success' },
    { variant: 'dot', tone: 'warning', className: 'bg-warning' },
    { variant: 'dot', tone: 'info', className: 'bg-info' },
    // count — each tone's muted text step, which holds 4.5:1 on the card.
    {
      variant: 'count',
      tone: 'neutral',
      className: 'text-neutral-muted-foreground',
    },
    {
      variant: 'count',
      tone: 'brand',
      className: 'text-primary-muted-foreground',
    },
    {
      variant: 'count',
      tone: 'danger',
      className: 'text-danger-muted-foreground',
    },
    {
      variant: 'count',
      tone: 'success',
      className: 'text-success-muted-foreground',
    },
    {
      variant: 'count',
      tone: 'warning',
      className: 'text-warning-muted-foreground',
    },
    {
      variant: 'count',
      tone: 'info',
      className: 'text-info-muted-foreground',
    },
    // quiet — each tone's muted text step for the label, which holds 4.5:1 on
    // the page and card surfaces.
    {
      variant: 'quiet',
      tone: 'neutral',
      className: 'text-neutral-muted-foreground',
    },
    {
      variant: 'quiet',
      tone: 'brand',
      className: 'text-primary-muted-foreground',
    },
    {
      variant: 'quiet',
      tone: 'danger',
      className: 'text-danger-muted-foreground',
    },
    {
      variant: 'quiet',
      tone: 'success',
      className: 'text-success-muted-foreground',
    },
    {
      variant: 'quiet',
      tone: 'warning',
      className: 'text-warning-muted-foreground',
    },
    {
      variant: 'quiet',
      tone: 'info',
      className: 'text-info-muted-foreground',
    },
  ],
  defaultVariants: {
    variant: 'soft',
    tone: 'neutral',
    badgeSize: 'md',
  },
});

type BadgeVariants = VariantProps<typeof badgeVariants>;

export { type BadgeVariants, badgeVariants };
